<template>
  <el-dialog
    v-model="dialogVisible"
    append-to-body
    class="market-detail-dialog"
    :close-on-click-modal="true"
    :show-close="false"
    width="800px"
    @close="close"
  >
    <button class="market-detail-dialog__close" type="button" @click="close">
      <i class="icon-close-circle-fill t-iconfont"></i>
    </button>

    <div class="market-detail-dialog__panel">
      <div class="market-detail-dialog__cover-wrap">
        <MediaCarousel :media-list="currentDetail.detailImage" />
      </div>

      <div class="market-detail-dialog__content">
        <div ref="contentRef" class="market-detail-dialog__body">
          <div class="market-detail-dialog__header">
            <div class="flex-center">
              <img
                :alt="currentDetail.name"
                class="market-detail-dialog__avatar"
                :src="currentDetail.icon"
              />

              <h2 class="market-detail-dialog__title">
                {{ currentDetail.name }}
              </h2>
            </div>
            <div class="market-detail-dialog__title-wrap">
              <span class="market-detail-dialog__tag">{{ currentDetail.tagLabel }}</span>
            </div>
          </div>

          <div class="market-detail-dialog__divider"></div>

          <!-- 内容 -->
          <div
            class="market-detail-dialog__description"
            :style="{
              maxHeight: `${maxContentHeight}px`
            }"
          >
            {{ currentDetail.details || currentDetail.profile }}
          </div>
        </div>

        <div class="market-detail-dialog__footer">
          <div
            class="market-detail-dialog__price"
            :class="[{ 'market-detail-dialog__price--empty': !currentDetail.cost }]"
          >
            {{ currentDetail.cost }}
          </div>

          <template v-if="!currentDetail.isAccess">
            <!-- 直接开通正式版 -->
            <button
              v-if="currentDetail.openFormal"
              class="market-detail-dialog__action"
              type="button"
              @click="freeOpen"
            >
              免费开通
            </button>
            <!-- 申请开通试用版 -->
            <template
              v-else-if="
                currentDetail.openTrial &&
                ['aiFindCustomerAssistant', 'aiBiddingAssistant'].includes(currentDetail.alias)
              "
            >
              <button class="market-detail-dialog__action" type="button" @click="openTrial">
                试用开通
              </button>

              <div v-if="currentDetail.trialDescribe" class="market-detail-dialog__trial-tip">
                <span>{{ currentDetail.trialDescribe }}</span>
              </div>

              <div v-if="currentDetail.status === 1" class="market-detail-dialog__expired-tip">
                <i
                  class="icon-information-line market-detail-dialog__expired-tip-icon t-iconfont"
                ></i>
                <span>套餐已到期</span>
              </div>
            </template>
            <!-- 留资申请开通 -->
            <button v-else class="market-detail-dialog__action" type="button" @click="leaveContact">
              申请开通
            </button>
          </template>

          <!-- 按钮组 -->
          <div v-else class="flex-center">
            <button
              class="market-detail-dialog__config-action"
              style="margin-right: 10px"
              type="button"
              @click="goSetting"
            >
              去配置
            </button>
            <button
              v-if="activeTab === 'aiAgent'"
              class="market-detail-dialog__work-action"
              type="button"
              @click="goUse"
            >
              去使用
            </button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
  <AntSet v-model:ant-drawer-visible="antDrawerVisible" :drawer-core-info="drawerCoreInfo"></AntSet>
</template>

<script setup>
import { nextTick, ref, useTemplateRef } from 'vue'
import MediaCarousel from '@/components/MediaCarousel.vue'
import AntSet from '../../integration-center/components/ant-set.vue'
import AppMessage from '@/components/message/index.js'
import { freeOpenApi, leaveContactApi, marketAuthApi } from '@/api/ai-board'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/store/useAppStore'
import emitter from '@/utils/mitt'

const contentRef = useTemplateRef('contentRef')

const props = defineProps({
  isAdmin: {
    type: Boolean,
    default: false
  },
  activeTab: {
    type: String,
    default: ''
  }
})

const maxContentHeight = ref(414)
const router = useRouter()
const dialogVisible = ref(false)
const currentDetail = ref({})
const drawerCoreInfo = ref(null)
const antDrawerVisible = ref(false)
const open = (detail = {}) => {
  currentDetail.value = detail
  dialogVisible.value = true
  nextTick(() => {
    maxContentHeight.value = Math.min(contentRef.value?.offsetHeight - 74, 414)
  })
  if (detail.isAccess) {
    getAuth(detail.alias)
  }
}

const close = () => {
  dialogVisible.value = false
}

const leaveContact = () => {
  leaveContactApi({
    name: currentDetail.value.name
  }).then((res) => {
    if (res.success) {
      AppMessage.success('已收到您的申请，将会在1-2个工作日内联系您')
    }
  })
}

const openTrial = async () => {
  if (props.isAdmin && currentDetail.value.status !== 1) {
    // 先关闭当前弹窗
    close()
    drawerCoreInfo.value = {
      alias: currentDetail.value.alias,
      title: currentDetail.value.name + (currentDetail.value.status === 2 ? '配置' : '开通'),
      feeType: currentDetail.value.feeType,
      status: currentDetail.value.status
    }
    antDrawerVisible.value = true
  } else if (currentDetail.value.status === 1) {
    AppMessage.warning('试用已到期，可联系专属顾问咨询开通事宜')
  } else {
    AppMessage.warning('请联系组织管理员开通试用')
  }
}

defineExpose({
  open,
  close
})

const authConfig = ref({
  assistantShow: false,
  configurationEdit: false,
  managementEdit: false
})
const getAuth = (alias) => {
  // 每次都先重置
  authConfig.value = {
    assistantShow: false,
    configurationEdit: false,
    managementEdit: false
  }
  marketAuthApi({
    alias
  }).then((res) => {
    if (res.success) {
      authConfig.value = res.result
    }
  })
}
const appStore = useAppStore()
const goSetting = () => {
  if (props.activeTab !== 'aiAgent' && props.activeTab !== 'dataSource') {
    AppMessage.warning('暂未配置')
    return
  }

  const permissionKey = props.activeTab === 'dataSource' ? 'configurationEdit' : 'managementEdit'

  if (!authConfig.value[permissionKey]) {
    AppMessage.warning('暂无权限，如有需要可联系组织管理员操作')
    return
  }

  if (props.activeTab === 'dataSource') {
    emitter.emit('navigateTo', {
      main: 'setting',
      sub: 'dataSourceManagement'
    })
  } else {
    emitter.emit('navigateTo', {
      main: 'setting',
      sub: 'assistantManagement'
    })
  }
}
const goUse = () => {
  // 根据别名来进行跳转不同的使用页面
  if (authConfig.value.assistantShow) {
    close()
    // 日程助手
    if (currentDetail.value.alias === 'aiScheduleAssistant') {
      AppMessage.warning('请前往手机端使用')
      return
    }
    // 蚂蚁找客和蚂蚁标讯助手
    if (
      currentDetail.value.alias === 'aiFindCustomerAssistant' ||
      currentDetail.value.alias === 'aiBiddingAssistant'
    ) {
      appStore.setThirdAppKey(currentDetail.value.appKey)
    }
    // 适配一个没有匹配到路由的情况
    const hasRoute = router.hasRoute(currentDetail.value.alias)
    emitter.emit('navigateTo', {
      main: 'chat',
      sub: hasRoute ? currentDetail.value.alias : undefined
    })
  } else {
    AppMessage.warning('暂无权限，如有需要可联系组织管理员操作')
  }
}

// 免费开通
const freeOpen = () => {
  freeOpenApi({
    feeType: currentDetail.value.feeType
  }).then((res) => {
    if (res.success) {
      AppMessage.success('开通成功')
      currentDetail.value.isAccess = 1
      getAuth(currentDetail.value.alias)
    }
  })
}
</script>

<style lang="scss" scoped>
:global(.market-detail-dialog.el-dialog) {
  border-radius: 20px;
  border: 1px solid #fff;
  background:
    linear-gradient(243deg, rgba(241, 223, 255, 0.25) 1%, rgba(255, 255, 255, 0.5) 14%),
    linear-gradient(193deg, rgba(182, 206, 255, 0.25) 19%, rgba(255, 255, 255, 0.5) 26%), #ffffff;
}

:global(.market-detail-dialog .el-dialog__body) {
  position: relative;
  padding: 0;
}

:global(.market-detail-dialog .el-dialog__header) {
  display: none;
}
.market-detail-dialog {
  &__close {
    position: absolute;
    top: -24px;
    right: -24px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;

    .t-iconfont {
      font-size: 24px;
      color: #ffffff;
    }
  }

  &__panel {
    display: grid;
    grid-template-columns: 400px 400px;
    width: 800px;
    min-height: 600px;
    overflow: hidden;
    border-radius: 20px;
    background:
      linear-gradient(193deg, rgba(182, 206, 255, 0.25) 19%, rgba(255, 255, 255, 0.5) 26%), #ffffff,
      linear-gradient(243deg, rgba(241, 223, 255, 0.25) 1%, rgba(255, 255, 255, 0.5) 14%);
  }

  &__cover-wrap {
    width: 400px;
    height: 600px;
    border-right: 1px solid #e5e6eb;
    background: #ffffff;
  }

  &__cover-fallback {
    width: 100%;
    height: 100%;
    padding: 32px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    background: linear-gradient(180deg, #f7f8fa 0%, #eef2ff 100%);
  }

  &__cover-fallback-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 24px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(114, 46, 209, 0.12);

    .t-iconfont {
      font-size: 40px;
      background: linear-gradient(315deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
      color: transparent;
      background-clip: text;
    }
  }

  &__cover-fallback-text {
    font-family: 'PingFang SC', sans-serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 34px;
    color: #323233;
    text-align: center;
  }

  &__content {
    display: flex;
    flex-direction: column;
    padding: 12px;
  }

  &__body {
    flex: 1;
    padding: 8px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__avatar,
  &__icon-box {
    width: 32px;
    height: 32px;
    flex: 0 0 auto;
    border-radius: 8px;
    border: 1px solid #f2f3f5;
    background: #ffffff;
    border: 1px solid #ffffff;
    box-shadow: 0px 0px 5.33px 0px rgba(56, 47, 220, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  &__avatar {
    border-radius: 999px;
    object-fit: cover;
  }

  &__icon {
    font-size: 20px;
    color: #111111;
  }

  &__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    flex-wrap: wrap;
  }

  &__title {
    margin-left: 12px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
    color: #323233;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 12px;
    border: 1px solid #e5e6eb;
    border-radius: 64px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 12px;
    line-height: 16px;
    color: #646566;
    white-space: nowrap;
  }

  &__divider {
    margin: 12px 0;
    border-bottom: 1px dashed #e5e6eb;
  }

  &__description {
    overflow: scroll;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    line-height: 24px;
    color: #646566;
    white-space: pre-wrap;
  }

  &__footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__price {
    min-height: 34px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 24px;
    font-weight: 600;
    line-height: 34px;
    color: #7a52f4;

    &--empty {
      visibility: hidden;
    }
  }

  &__expired-tip {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    min-height: 16px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 12px;
    line-height: 16px;
    color: #f54e4e;
  }

  &__expired-tip-icon {
    font-size: 16px;
    color: #f54e4e;
  }

  &__trial-tip {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 16px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 12px;
    line-height: 16px;
    background: linear-gradient(273deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
    background-clip: text;
    color: transparent;
  }

  &__action {
    height: 40px;
    border: 0;
    border-radius: 12px;
    background: url(@/assets/free-open.png) center;
    background-size: 100% 100%;
    color: #ffffff;
    font-size: 14px;

    line-height: 20px;
    cursor: pointer;
    transition:
      transform 0.2s ease,
      opacity 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      opacity: 0.92;
      box-shadow: 0 4px 10px rgba(22, 7, 43, 0.2);
    }

    &:active {
      transform: translateY(1px);
      opacity: 0.88;
      box-shadow: none;
    }
  }

  &__config-action {
    display: block;
    width: 100%;
    height: 40px;
    padding: 6px 12px;
    border: 1px solid #e5e6eb;
    border-radius: 12px;
    background: #ffffff;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #646566;
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      color 0.2s ease,
      background-color 0.2s ease;

    &:hover {
      border-color: #c9cdd4;
      color: #323233;
      background: #f7f8fa;
    }

    &:active {
      border-color: #e5e6eb;
      color: #323233;
      background: #f2f3f5;
    }
  }
  &__work-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 40px;
    padding: 6px 12px;
    border: 0;
    border-radius: 12px;
    background: #7a52f4;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #ffffff;
    cursor: pointer;
    transition:
      transform 0.2s ease,
      opacity 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      opacity: 0.92;
      box-shadow: 0 4px 10px rgba(114, 46, 209, 0.2);
    }

    &:active {
      transform: translateY(1px);
      opacity: 0.88;
      box-shadow: none;
    }
  }
}
</style>
